import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState, type CSSProperties, type TransitionEvent } from 'react'
import { assertNever } from '../../../utils/assertNever'
import { forceReflow } from '../../../utils/forceReflow'
import style from './Collapsible.module.sass'

type CollapsibleState =
	| 'close'
	| 'before-open-transition'
	| 'open-transition'
	| 'open'
	| 'before-close-transition'
	| 'close-transition'

export const Collapsible = React.memo(function Collapsible(props: {
	children: React.ReactNode
	open: boolean
	hasLowerIndex?: boolean
	hasHighIndex?: boolean
	type?: 'revealTopFirst' | 'revealBottomFirst'
	slowerAnimation?: boolean
}) {
	const [state, setState] = useState<CollapsibleState>(props.open ? 'open' : 'close')
	const [contentHeight, setContentHeight] = useState('auto')
	const contentRef = useRef<HTMLDivElement>(null)

	const updateContentHeight = useCallback(() => {
		if (!contentRef.current) {
			return
		}
		const contentHeight = `${contentRef.current.getBoundingClientRect().height}px`
		setContentHeight(contentHeight)
	}, [])

	const onTransitionEnd = useCallback(
		(event: TransitionEvent) => {
			if (event.propertyName === 'visibility') {
				setState(props.open ? 'open' : 'close')
			}
		},
		[props.open],
	)

	useEffect(() => {
		if (state === 'close') {
			if (props.open) {
				updateContentHeight()
				setState('before-open-transition')
			}
		} else if (state === 'before-open-transition') {
			if (props.open) {
				forceReflow(contentRef.current)
				setState('open-transition')
			}
		} else if (state === 'open-transition') {
			if (!open) {
				setState('close')
			}
		} else if (state === 'open') {
			if (!props.open) {
				updateContentHeight()
				setState('before-close-transition')
			}
		} else if (state === 'before-close-transition') {
			if (!props.open) {
				forceReflow(contentRef.current)
				setState('close-transition')
			}
		} else if (state === 'close-transition') {
			if (props.open) {
				setState('open')
			}
		} else {
			assertNever(state)
		}
	}, [props.open, state, updateContentHeight])

	return (
		<div
			className={clsx(
				style.wrapper,
				style[`is_state_${state}`],
				style[`is_type_${props.type}`],
				props.slowerAnimation && style.wrapperSlower,
				props.hasLowerIndex && style.wrapperIndexLow,
				props.hasHighIndex && style.wrapperIndexHigh,
			)}
			style={
				{
					'--Collapsible-content-height': contentHeight,
				} as CSSProperties // Custom properties not supported workaround
			}
			onTransitionEnd={onTransitionEnd}
			aria-hidden={!props.open}
		>
			<div className={style.content} ref={contentRef}>
				{props.children}
			</div>
		</div>
	)
})
